/**
 * Phase I Wave 1.22 (F85) · V12 Coming Soon Registry.
 *
 * Single source of truth that maps a slug to the metadata displayed by
 * `V12ComingSoonPage`. Each entry corresponds to a sidebar nav item
 * whose dedicated V12 page has not been shipped yet · the registry
 * drives both:
 *
 *   • App.js  · the batch `<Route>` registration (one entry → one Route)
 *   • V12ComingSoonPage · the page copy, icon and estimated wave
 *   • v12_user_nav.js · the canonical `/v2/<slug>` target of every
 *     sidebar item (no more `legacyHref` full-reloads)
 *
 * Design notes
 * ------------
 *   • `slug` is also used as the url param behind `/v2/*` · keep it
 *     kebab-case and stable (shared with analytics).
 *   • `cluster` matches the sidebar group id · used by the placeholder
 *     to show a "back to <cluster>" breadcrumb.
 *   • `estimatedWave` is a short label operators use for roadmap
 *     expectations ("Phase I Wave 2", "Phase II", etc.). Shown as a
 *     muted sub-caption on the placeholder page.
 *   • `icon` is a lucide-react icon name resolved by the page via
 *     `ICON_MAP`. Keep names in sync with the sidebar config.
 */

export const V12_COMING_SOON_REGISTRY = {
    // ─── Workspace ──────────────────────────────────────────────────
    // Phase VI.5 Wave A · "notifications" slug promoted to REAL alias
    // (mounts <Notifications/> on /v2/notifications · see App.js).
    // Entry preserved as comment for audit traceability.

    // ─── Lead & Outreach ────────────────────────────────────────────
    // Phase I Wave 1.28 (F91) · 6 items are now REAL pages wired under
    // /v2/leads and /v2/outreach/*. Only the 2 M10 legacy bridges stay
    // as enriched placeholders with a `classicPath` SPA shortcut.
    "leads-m10": {
        path: "/app/leads/m10",
        title: "Leads · M10",
        subtitle: "Pipeline lead M10 · generation + enrichment avanzato.",
        icon: "Users",
        cluster: "lead-outreach",
        estimatedWave: "Phase I Wave 3",
        legacyEscape: "/admin/leads",
    },
    "outreach-m10": {
        path: "/app/outreach/m10-sequences",
        title: "Sequenze outreach · M10",
        subtitle: "Sequenze M10 con AI reply handling e split-testing.",
        icon: "Mail",
        cluster: "lead-outreach",
        estimatedWave: "Phase I Wave 3",
        legacyEscape: "/admin/outreach/sequences",
    },

    // ─── CRM ────────────────────────────────────────────────────────
    // Phase I Wave 1.23 (F86) · 6 CRM items are now REAL pages wired
    // under `/v2/crm/<italian-slug>` · see `pages/v12/crm/*`.
    // Keeping the comment here so operators searching for "crm-contacts"
    // land on the SEAL f86_phase_i_wave1_crm_cluster.md.

    // ─── AI Suite ───────────────────────────────────────────────────
    // Phase I Wave 1.29 (F92) · 4 items are now REAL pages:
    //   ai-universe, ai-badges, ai-leaderboard, ai-prompts.
    // Phase VI.5 Wave A · "ai-civilization" slug promoted to REAL alias
    // (mounts <Civilization/> wrapped in ModuleGate on /v2/ai/civilization).

    // ─── Funnel & Decisioni (admin-only) ─────────────────────────────
    "funnel-ai-decisions": {
        path: "/app/funnel/ai-decisions",
        title: "AI Decisions",
        subtitle: "Log decisioni AI sui lead e sui funnel · trasparenza totale.",
        icon: "Sparkles",
        cluster: "funnel-decisioni",
        estimatedWave: "Phase I Wave 3",
        legacyEscape: "/admin/crm/ai-decisions",
    },
    "funnel-workflows": {
        path: "/app/funnel/workflows",
        title: "Workflow",
        subtitle: "Visual workflow builder · automazioni cross-canale.",
        icon: "Workflow",
        cluster: "funnel-decisioni",
        estimatedWave: "Phase I Wave 3",
        legacyEscape: "/admin/crm/workflows",
    },
    "funnel-policies": {
        path: "/app/funnel/policies",
        title: "Policy",
        subtitle: "Policy di guardrail e compliance per le automazioni.",
        icon: "ScrollText",
        cluster: "funnel-decisioni",
        estimatedWave: "Phase I Wave 3",
        legacyEscape: "/admin/crm/policies",
    },
    "funnel-approvals": {
        path: "/app/funnel/approvals",
        title: "Approvazioni",
        subtitle: "Richieste approvazione · gating human-in-the-loop.",
        icon: "ShieldCheck",
        cluster: "funnel-decisioni",
        estimatedWave: "Phase I Wave 3",
        legacyEscape: "/admin/crm/approvals",
    },
    "funnel-audit": {
        path: "/app/funnel/audit",
        title: "Audit funnel",
        subtitle: "Timeline audit · chi ha fatto cosa e quando.",
        icon: "ScrollText",
        cluster: "funnel-decisioni",
        estimatedWave: "Phase I Wave 3",
        legacyEscape: "/admin/crm/audit",
    },
    "funnel-supervisor": {
        path: "/app/funnel/supervisor",
        title: "Supervisor",
        subtitle: "Panoramica supervisor · segnala anomalie e drift.",
        icon: "ShieldCheck",
        cluster: "funnel-decisioni",
        estimatedWave: "Phase I Wave 3",
        legacyEscape: "/admin/crm/supervisor",
    },

    // ─── PRESS ──────────────────────────────────────────────────────
    // Phase I Wave 1.29 (F92) · 3 items are now REAL pages wired to
    // /api/f92/press/* · see pages/v12/press/*. No placeholder here.

    // ─── Distribuzione (admin-only) ──────────────────────────────────
    "distribution-jobs": {
        path: "/app/distribution/jobs",
        title: "Lavori distribuzione",
        subtitle: "Queue dei job di distribuzione · status e retry.",
        icon: "Workflow",
        cluster: "distribuzione",
        estimatedWave: "Phase I Wave 5",
        legacyEscape: "/admin/distribution/jobs",
    },
    "distribution-channels": {
        path: "/app/distribution/channels",
        title: "Canali distribuzione",
        subtitle: "Configura canali · email, webhook, RSS, Atom, social.",
        icon: "Layers",
        cluster: "distribuzione",
        estimatedWave: "Phase I Wave 5",
        legacyEscape: "/admin/distribution/channels",
    },
    "distribution-email": {
        path: "/app/distribution/email",
        title: "Email distribution",
        subtitle: "Configurazione provider email · SPF, DKIM, warmup.",
        icon: "Mail",
        cluster: "distribuzione",
        estimatedWave: "Phase I Wave 5",
        legacyEscape: "/admin/distribution/email",
    },
    "distribution-webhook": {
        path: "/app/distribution/webhook",
        title: "Webhook distribution",
        subtitle: "Config webhook outbound · retry policy e signing.",
        icon: "Webhook",
        cluster: "distribuzione",
        estimatedWave: "Phase I Wave 5",
        legacyEscape: "/admin/distribution/webhook",
    },
    "distribution-rss": {
        path: "/app/distribution/rss",
        title: "RSS feed",
        subtitle: "Feed RSS automatici dalle tue press releases.",
        icon: "Mail",
        cluster: "distribuzione",
        estimatedWave: "Phase I Wave 5",
        legacyEscape: "/admin/distribution/rss",
    },
    "distribution-atom": {
        path: "/app/distribution/atom",
        title: "Atom feed",
        subtitle: "Feed Atom per lettori più moderni.",
        icon: "Mail",
        cluster: "distribuzione",
        estimatedWave: "Phase I Wave 5",
        legacyEscape: "/admin/distribution/atom",
    },

    // ─── Autorevolezza (admin-only) ──────────────────────────────────
    "authority-policies": {
        path: "/app/authority/policies",
        title: "Policy autorevolezza",
        subtitle: "Policy di content moderation e authority scoring.",
        icon: "ScrollText",
        cluster: "autorevolezza",
        estimatedWave: "Phase I Wave 5",
        legacyEscape: "/admin/distribution/policies",
    },
    "authority-supervisor": {
        path: "/app/authority/supervisor",
        title: "Supervisor autorevolezza",
        subtitle: "Dashboard supervisor · anomalie e manual override.",
        icon: "ShieldCheck",
        cluster: "autorevolezza",
        estimatedWave: "Phase I Wave 5",
        legacyEscape: "/admin/distribution/supervisor",
    },
    "authority-decisions": {
        path: "/app/authority/decisions",
        title: "Decisioni autorevolezza",
        subtitle: "Log decisioni autorevolezza · trasparenza AI.",
        icon: "Sparkles",
        cluster: "autorevolezza",
        estimatedWave: "Phase I Wave 5",
        legacyEscape: "/admin/distribution/decisions",
    },
    "authority-approvals": {
        path: "/app/authority/approvals",
        title: "Approvazioni autorevolezza",
        subtitle: "Richieste approvazione · publish gating.",
        icon: "ShieldCheck",
        cluster: "autorevolezza",
        estimatedWave: "Phase I Wave 5",
        legacyEscape: "/admin/distribution/approvals",
    },
    "authority-workflows": {
        path: "/app/authority/workflows",
        title: "Workflow autorevolezza",
        subtitle: "Workflow di content governance.",
        icon: "Workflow",
        cluster: "autorevolezza",
        estimatedWave: "Phase I Wave 5",
        legacyEscape: "/admin/distribution/workflows",
    },

    // ─── Marketplace & Creator ───────────────────────────────────────
    // Phase VI.5 Wave A · 4 marketplace items promoted to REAL aliases
    // in App.js (mount <Marketplace/> + <MarketplaceLibrary/>). Creator
    // Studio + Earnings stay as placeholders because the real pages
    // (`v9/admin/marketplace/agents/CreatorStudio.jsx` and
    // `CreatorRoyalties.jsx`) are `adminOnly` · the sidebar now flags
    // them with `requiredRoles: ["admin","super_admin"]` so a plain
    // user never sees the link. See phase_vi_5_wave_a_seal.md.
    "marketplace-creator-studio": {
        path: "/app/marketplace/creator-studio",
        title: "Creator Studio",
        subtitle: "Pubblica i tuoi moduli e monetizza come creator.",
        icon: "Sparkles",
        cluster: "marketplace-creator",
        estimatedWave: "Phase II",
        classicPath: "/creator/dashboard",
    },
    "marketplace-creator-earnings": {
        path: "/app/marketplace/creator-earnings",
        title: "Guadagni creator",
        subtitle: "Dashboard earnings · revenue share e payout.",
        icon: "Coins",
        cluster: "marketplace-creator",
        estimatedWave: "Phase II",
        classicPath: "/creator/earnings",
    },

    // ─── Content ────────────────────────────────────────────────────
    // Phase VI.5 Wave A · "content-blog" + "content-articles" promoted
    // to REAL aliases (mount <Blog/> on both V12 paths · see App.js).

    // ─── Partnership ────────────────────────────────────────────────
    // Phase I Wave 1.29 (F92) · materials + payout are REAL.
    // Phase VI.5 Wave A · 3 partnership items promoted to REAL aliases
    // (mount <Affiliate/> on /v2/partnership/affiliate and
    // <AffiliateDashboardV2/> on affiliate-v2 + dashboard · see App.js).
    // partnership-materials + partnership-payout are REAL (F92).

    // ─── Account ────────────────────────────────────────────────────
    // Phase I Wave 1.25 (F88) · 7 Account items are now REAL pages wired
    // under `/v2/account/<italian-slug>` + 1 redirect (prezzi → subscription).
    // See SEAL f88_phase_i_wave1_account_cluster.md.

    // ─── Academy ────────────────────────────────────────────────────
    // Phase I Wave 1.29 (F92) · 3 items are now REAL pages wired to
    // /api/f92/academy/* · see pages/v12/academy/*. No placeholder.

    // ─── Community ──────────────────────────────────────────────────
    // Phase I Wave 1.29 (F92) · 5 items are now REAL pages wired to
    // /api/f92/community/* · see pages/v12/community/*. No placeholder.
};

/**
 * Returns the entry for a slug · or null if unknown.
 */
export function getComingSoonEntry(slug) {
    return V12_COMING_SOON_REGISTRY[slug] || null;
}

/**
 * Returns all entries as an array · useful for App.js batch route
 * registration.
 */
export function listComingSoonEntries() {
    return Object.entries(V12_COMING_SOON_REGISTRY).map(
        ([slug, entry]) => ({ slug, ...entry }),
    );
}
